/**
 * Recording length (seconds, fractional) → station-rate spot length.
 *
 * Mirrors get_stationrates.derive_spot_length_from_audio in the backend:
 *   < 20s → 15, < 35s → 30, ≤ 65s → 60, anything longer is rejected (no rate).
 */
export type SpotLength = 15 | 30 | 60

/** Longest recording (seconds) the backend will price; longer audio has no matching rate. */
export const MAX_SPOT_AUDIO_SECONDS = 65

/**
 * Encoders pad the last frame, so a 65.0 s commercial exported as MP3 measures ~65.05 s.
 * Lengths within this tolerance of the maximum count as the maximum (backend:
 * AUDIO_LENGTH_TOLERANCE_SECONDS). Use exceedsMaxSpotAudioSeconds() for every length check.
 */
export const AUDIO_LENGTH_TOLERANCE_SECONDS = 0.1

export function exceedsMaxSpotAudioSeconds(length: number): boolean {
  return length > MAX_SPOT_AUDIO_SECONDS + AUDIO_LENGTH_TOLERANCE_SECONDS
}

export function spotLengthForAudioSeconds(length: number | null | undefined): SpotLength | null {
  if (length == null || !Number.isFinite(length) || length < 0) return null
  if (length < 20) return 15
  if (length < 35) return 30
  if (!exceedsMaxSpotAudioSeconds(length)) return 60
  return null
}

export type SpotLengthCategory = "short" | "medium" | "long" | "other"

export function spotLengthCategoryForAudioSeconds(length: number | null | undefined): SpotLengthCategory {
  switch (spotLengthForAudioSeconds(length)) {
    case 15:
      return "short"
    case 30:
      return "medium"
    case 60:
      return "long"
    default:
      return "other"
  }
}
